import React, { ChangeEvent, useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { actionSignin } from '../../actions/auth'
import { AppDispatch } from '../../reducers/store'
import { adminValidatePassword, adminValidateUsername } from '../../components/validations/admins/login'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { Button, FormField, Loader } from '../../components'

import './accountLogin.css'

interface PostDataParams {
  username: string;
  password: string;
}

const AccountLogin = () => {
  const pageName                                 = "Login"
  const dispatch                                 = useDispatch<AppDispatch>();
  const location                                 = useLocation();
  const [hideShow, setHideShow]                  = useState<boolean>(false);
  const [savingInfo, setSavingInfo]              = useState<boolean>(false);
  const [usernameErrors, setUsernameErrors]      = useState<string[]>([]);
  const [passwordErrors, setPasswordErrors]      = useState<string[]>([]);
  const [isButtonDisabled, setIsButtonDisabled]  = useState<boolean>(true);
  const [errorMessage, setErrorMessage]          = useState(false);

  const [postData, setPostData]                  = useState<PostDataParams>({ username: '', password: '' })
  const [isLoadingBtn, setIsLoadingBtn]          = useState({ username: false, password: false });
  const [isValid, setIsValid]                    = useState({ username: false, password: false });

  // State to track whether the user is redirected from the registration page
  const [isRegistered, setIsRegistered]          = useState(false);

  useEffect(() => {
    //this is coming from the action method to ensure that the user gets the registration message 
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.get("verification") === "true") {
      setIsRegistered(true);
    }
  }, [location.search]);

  const useDebounce = <T,>(value: T, delay: number): T => {
    const [debounced, setDebounced] = useState(value);
  
    useEffect(() => {
      const handler = setTimeout(() => {
        setDebounced(value);
      }, delay);
      return () => clearTimeout(handler);
    }, [value, delay]);
  
    return debounced;
  };

  const debouncedPostData = useDebounce<PostDataParams>(postData, 500);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setIsLoadingBtn((prev) => ({ ...prev, [name]: true }));
    setPostData((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    const validateUsername = () => {
      const { isValid, errors } = adminValidateUsername(debouncedPostData?.username);
      setIsValid((prevState) => ({ ...prevState, username: isValid}));
      setUsernameErrors(errors);
      setIsLoadingBtn((prevState) => ({ ...prevState, username: false}));
      return errors;
    };

    const validatePassword = () => {
      const { isValid, errors } = adminValidatePassword(debouncedPostData?.password);
      setIsValid((prevState) => ({ ...prevState, password: isValid}));
      setPasswordErrors(errors);
      setIsLoadingBtn((prevState) => ({ ...prevState, password: false }));
      return errors;
    };

    const usernameErrors = validateUsername();
    const passwordErrors = validatePassword();

    setUsernameErrors(usernameErrors);
    setPasswordErrors(passwordErrors);

    const hasFormErrors = usernameErrors.length > 0 || passwordErrors.length > 0;
    setIsButtonDisabled(hasFormErrors);
  }, [debouncedPostData]);
  
  const handleSubmit = async(e: ChangeEvent<any>) => {
    e.preventDefault();
    setSavingInfo(true);
    setIsButtonDisabled(true)
    const res: any = await dispatch(actionSignin({postData, location}))
    if(res?.status === 400 || res?.status === 404){
      setErrorMessage(res?.message)
      setSavingInfo(false);
    }else{
      setSavingInfo(true);
      setErrorMessage(false)
    }
  }

  return (
    <>
      <title>{pageName}</title>
      <div className='account-container'>
        <form className='account-login-container' onSubmit={handleSubmit} autoComplete="off" >
          <div className='account-login-container-title'>
              <h3>Login</h3>
          </div>

          <div className='account-login-input-btn-pass-container'>
          <FormField inputType type="text" labelName="Username" name="username" value={postData.username} handleChange={handleChange} isLoadingBtn={isLoadingBtn.username} isValid={isValid.username} errors={usernameErrors || []} />

          <div className='account-login-group-wrapper'>
            <FormField inputType type={hideShow ? 'text' : 'password'} labelName="Password" name="password" value={postData.password} handleChange={handleChange} isLoadingBtn={isLoadingBtn.password} isValid={isValid.password} errors={passwordErrors || []} />
            <div className='account-login-show-hide-pass-container'>
            <span className={ postData.password.length > 0 ? 'show-hide-password unlock' : 'show-hide-password' } onClick={() => setHideShow((prev) => !prev)}>{hideShow ? <VisibilityOff /> : <Visibility />}</span>
            </div>
          </div>

          <Button onClickButton buttonClickWrap={savingInfo ? `button-login-submitted` : `button-login-submit`} onClickName={savingInfo ? <>{<Loader />} Signing in...</> : "Sign in"} isButtonDisabled={isButtonDisabled} buttonClasses={savingInfo ? ['button-disabled'] : (isButtonDisabled ? ['buttonDisabledClass'] : ['buttonEnabledClass'])} disabled={savingInfo} />
          
          {errorMessage && (
            <div className="form-response-message">
            <p className="error-msg">{errorMessage}</p>
            </div>
          )}
          {isRegistered && (
            <div className="registration-success-message">
            <p className="success-msg">Your registration was successful, please wait for an approval email.</p>
            </div>
          )}
          </div>
          <Link className='forgot-password' to="/forgot-password">forgot password?</Link>
          <div className='have-account-wrapper'>
            <span>Don't have an account?</span>
            <Link className='account-reg-log' to="/register">Sign up</Link>
          </div>
        </form>
      </div>
    </>
  )
}

export default AccountLogin